import { useEffect, useRef } from "react";
import { useAuth, useStoredAuth } from "#/hooks/use-auth";
import { useMe } from "#/hooks/use-me";
import {
	analytics,
	buildSignupPayload,
	getAttribution,
	hasFiredSignup,
	isNewAccount,
	markSignupFired,
} from "#/lib/analytics";

/**
 * Fires `sign_up` / `login` exactly once per established session, plus
 * `identify()`. Lives inside `_app.tsx` because it depends on
 * `useMe()` / `useAuth()`.
 *
 * New-vs-returning is decided by the `created_at` heuristic (see
 * `lib/analytics/auth-funnel.ts`). Dedup is two-layered:
 *   - `handledUserRef` — one decision per mounted session (defends against
 *     `/auth/me` refetch / re-render storms).
 *   - localStorage flag (in `hasFiredSignup`/`markSignupFired`) — makes `sign_up`
 *     exactly-once per account across sessions.
 *
 * See `src/lib/analytics/ANALYTICS.md` → "The signup funnel".
 */
export function AuthFunnelSync() {
	const { isAuthenticated, userId } = useAuth();
	const stored = useStoredAuth();
	const me = useMe();
	const handledUserRef = useRef<string | null>(null);

	useEffect(() => {
		if (!isAuthenticated || !userId) {
			handledUserRef.current = null;
			return;
		}
		if (!me.data) return; // wait for /auth/me (it carries created_at)
		if (handledUserRef.current === userId) return; // already handled this session

		const traits = {
			user_id: userId,
			wallet_address: me.data.wallet_address,
			referred_by_code: me.data.referred_by_code,
			account_created_at: me.data.created_at,
		};

		// Prefer the server-authoritative flag; fall back to the created_at
		// heuristic only when the backend hasn't supplied it yet.
		const serverNew = stored?.is_new_account;
		const isNew =
			typeof serverNew === "boolean"
				? serverNew
				: isNewAccount(me.data.created_at);

		if (isNew && !hasFiredSignup(userId)) {
			analytics.track(
				"sign_up",
				buildSignupPayload(me.data.referred_by_code, getAttribution()),
			);
			markSignupFired(userId);
			analytics.identify({ ...traits, is_new_account: true });
		} else {
			analytics.track("login", { method: "email" });
			analytics.identify({ ...traits, is_new_account: false });
		}

		handledUserRef.current = userId;
	}, [isAuthenticated, userId, me.data, stored]);

	return null;
}
